import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { StatementPicker } from "./StatementPicker";
import { uploadFiles } from "@/lib/uploads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AU_PHONE_RE = /^(?:\+?61|0)[2-478]\d{8}$/;
const normalizePhone = (v) => v.replace(/[\s()\-.]/g, "");
const EMPTY = { name: "", email: "", phone: "", message: "", website: "" };

const fieldClass =
  "w-full bg-transparent border-b border-white/25 focus:border-[#F9F8F4] outline-none py-3 text-base md:text-sm text-[#F9F8F4] placeholder:text-white/80 transition-[border-color] duration-300";
const labelClass = "block text-[10px] uppercase tracking-[0.25em] text-white mb-1";

const Field = ({ id, label, ...props }) => (
  <div>
    <label htmlFor={id} className={labelClass}>{label}</label>
    <input id={id} data-testid={`${id}-input`} className={fieldClass} {...props} />
  </div>
);

export const ClientUploadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Please attach at least one document.");
    if (!AU_PHONE_RE.test(normalizePhone(form.phone))) return toast.error("Please enter a valid Australian phone number.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/client-uploads`, { ...form, file_count: files.length });
      const { failed, done } = await uploadFiles("client-uploads", data.id, data.upload_token, files, setProgress);
      if (!done || failed === files.length) throw new Error("upload failed");
      if (failed) toast.error(`${failed} file${failed > 1 ? "s" : ""} couldn't be uploaded — please try those again.`);
      setSent(files.length - failed);
      setFiles([]);
      setForm(EMPTY);
    } catch {
      toast.error("Your documents couldn't be sent. Please try again in a moment.");
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  if (sent) {
    return (
      <div data-testid="client-upload-success" className="border border-white/15 p-8 md:p-10 text-[#F9F8F4]">
        <CheckCircle2 size={28} className="text-[#A78558]" />
        <p className="mt-5 font-serif text-2xl md:text-3xl">Thank you — {sent} document{sent > 1 ? "s" : ""} received.</p>
        <p className="mt-3 text-sm text-white">Kevin has been sent your documents and will be in touch if anything else is needed.</p>
        <button
          type="button"
          data-testid="client-upload-again-button"
          onClick={() => setSent(0)}
          className="mt-8 text-xs uppercase tracking-[0.15em] text-white underline decoration-[#A78558] decoration-2 underline-offset-8"
        >
          Upload more documents
        </button>
      </div>
    );
  }

  return (
    <form data-testid="client-upload-form" onSubmit={submit} className="space-y-9">
      <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="client-upload-website">Website</label>
        <input id="client-upload-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-9">
        <Field id="client-upload-name" label="Full name" type="text" required minLength={2} value={form.name} onChange={update("name")} placeholder="Jane Citizen" />
        <Field id="client-upload-email" label="Email" type="email" required value={form.email} onChange={update("email")} placeholder="jane@example.com.au" />
        <Field id="client-upload-phone" label="Phone" type="tel" required value={form.phone} onChange={update("phone")} placeholder="04 0000 0000" />
      </div>
      <div>
        <label htmlFor="client-upload-message" className={labelClass}>Short note (optional)</label>
        <textarea
          id="client-upload-message"
          data-testid="client-upload-message-input"
          rows={3}
          maxLength={4000}
          value={form.message}
          onChange={update("message")}
          placeholder="e.g. Here are the super statements you asked for."
          className={`${fieldClass} resize-none`}
        />
      </div>
      <StatementPicker
        files={files}
        onChange={setFiles}
        onError={toast.error}
        progress={progress}
        label="Documents"
        hint="Photos, PDFs, Word or Excel files"
        testid="client-upload"
      />
      <button
        data-testid="client-upload-submit-button"
        type="submit"
        disabled={sending}
        className="inline-flex items-center gap-2 rounded-full bg-[#F9F8F4] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,transform,opacity] duration-300 hover:bg-white hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {sending ? "Uploading" : "Send documents"}
        {sending ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
      </button>
    </form>
  );
};
